/* eslint-disable array-callback-return */
import { Badge } from "ui/badge";
import {
  Plus,
  Trash2,
  Search,
  X,
  Edit2,
  Check,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "ui/switch";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Label } from "ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import { useScreenPermission } from "hooks/useScreenPermission";
import { createCurrencyHead, HeadCurrency, CurrencyHead, getCurrencyHeadList, deleteCurrencyHead, UpdateCurrencyHead, addCurrencyToHead, getAllCurrencies, Currency, deleteCurrency, UpdateCurrencyToHead } from "services/currencyService";
import axios from "axios";
import useDebounce from "hooks/useDebounce";
import { FullScreenLoading } from "components/ui/loader";
import { useAuth } from "contexts/AuthContext";
import { InputFieldset } from "ui/inputFieldset";
import { TextareaUI } from "ui/textareaUI";
import { SelectFieldset } from "ui/selectFieldset";
import { removeSpecialCharacters } from "utils/commonutils";
import { usePageHeader } from "contexts/PageHeaderContext";


export const CurrencyHeadMasterPage = () => {
  const { user } = useAuth();
  const { setPageHeader } = usePageHeader();
  const [loading, setLoading] = useState(false);
  const [refetch, setRefatch] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddHeadDialogOpen, setIsAddHeadDialogOpen] = useState(false);
  const [isEditHeadDialogOpen, setIsEditHeadDialogOpen] = useState(false);
  const [isAddCurrencyDialogOpen, setIsAddCurrencyDialogOpen] = useState(false);
  const [selectedHead, setSelectedHead] = useState<CurrencyHead | null>(null);
  const [editingHead, setEditingHead] = useState<CurrencyHead | null>(null);
  const [editingCurrencyId, setEditingCurrencyId] = useState<string | null>(
    null,
  );
  const [editingUnitRate, setEditingUnitRate] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "head" | "currency";
    headId?: string;
    currencyId?: string;
  } | null>(null);
  const [deletingItemName, setDeletingItemName] = useState<string | null>(null);

  const { screenName, icon: ScreenIcon } = useScreenPermission();

  // Sorting state
  const [sortField, setSortField] = useState<"headName" | "currency" | null>(
    null,
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const [headErrors, setHeadErrors] = useState({
    headName: "",
    description: "",
  });

  const [currencyErrors, setCurrencyErrors] = useState({
    currencyCode: "",
    unitRate: "",
  });

  // Form data for head
  const [headFormData, setHeadFormData] = useState({
    headName: "",
    description: "",
    isActive: true,
    isUnit: false,
  });

  // Form data for currency
  const [currencyFormData, setCurrencyFormData] = useState({
    currencyId: 0,          // ✅ add this
    currencyCode: "",
    currencySymbol: "",
    unitRate: "",
  });

  const [currencyHeads, setCurrencyHeads] = useState<CurrencyHead[]>([]);

  const mergedFilters = useMemo(
    () => ({ searchTerm }),
    [searchTerm],
  );
  const debouncedFilters = useDebounce(mergedFilters, 400);

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      const data = await getAllCurrencies();
      setCurrencies(data);
    };
    fetchCurrencies();
  }, []);


  // GET API CURRENCY
  const fetchFiltersCounts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCurrencyHeadList(debouncedFilters);
      const items = (Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : []) as Array<Record<string, unknown>>;
      setCurrencyHeads(
        items.map((head) => {
          const rawCurrencies = Array.isArray(head?.currencies) ? head.currencies : [];
          const rawDetails = Array.isArray(head?.details)
            ? (head.details as Array<Record<string, unknown>>)
            : [];
          return {
            ...head,
            isUnit: head?.isUnit ?? false,
            currencies:
              rawCurrencies.length > 0
                ? rawCurrencies
                : rawDetails.map((detail) => {
                  const detailRecord = detail as Record<string, unknown>;
                  return {
                    publicId: String(
                      detailRecord["publicId"] ??
                      detailRecord["detailPublicId"] ??
                      ""
                    ),
                    id: String(
                      detailRecord["currencyHeadDetailId"] ??
                      detailRecord["currencyId"] ??
                      detailRecord["id"] ??
                      "",
                    ),
                    currencyId: Number(
                      detailRecord["currencyId"] ?? 0
                    ),
                    currencyCode: String(
                      detailRecord["currencyCode"] ??
                      detailRecord["currencyName"] ??
                      "",
                    ),
                    currencySymbol: String(detailRecord["currencySymbol"] ?? ""),
                    unitRate: String(
                      detailRecord["unitRate"] ?? detailRecord["unRate"] ?? "",
                    ),
                  };
                }),
          };
        }) as CurrencyHead[],
      );
    } catch {
      toast.error("Failed to fetch draft visa applications");
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  useEffect(() => {
    fetchFiltersCounts()
  }, [fetchFiltersCounts, refetch]);


  // Sorting functions
  const handleSort = (field: "headName" | "currency") => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else if (sortOrder === "desc") {
        setSortOrder(null);
        setSortField(null);
      } else {
        setSortOrder("asc");
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // const sortedLoanApplicants = useMemo(() => {
  //     if (!sortColumn) return loanTypes;
  //     // eslint-disable-next-line @typescript-eslint/no-explicit-any
  //     return [...loanTypes].sort((a: any, b: any) => {
  //       const aValue = String(a[sortColumn] ?? "");
  //       const bValue = String(b[sortColumn] ?? "");
  
  //       if (aValue == null) return 1;
  //       if (bValue == null) return -1;
  
  //       const comparison = String(aValue).localeCompare(
  //         String(bValue),
  //         undefined,
  //         { numeric: true, sensitivity: "base" }
  //       );
  
  //       return sortDirection === "asc"
  //         ? comparison
  //         : -comparison;
  //     });
  //   }, [loanTypes, sortColumn, sortDirection]);

  const getSortIcon = (field: "headName" | "currency") => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 ml-1 inline opacity-40" />;
    }
    if (sortOrder === "asc") {
      return <ChevronUp className="w-3 h-3 ml-1 inline text-[#5B9BD5]" />;
    }
    if (sortOrder === "desc") {
      return <ChevronDown className="w-3 h-3 ml-1 inline text-[#5B9BD5]" />;
    }
    return <ArrowUpDown className="w-3 h-3 ml-1 inline opacity-40" />;
  };


  const handleAddHead = async () => {
    const errors = {
      headName: "",
      description: "",
    };

    // Validate head name
    if (!headFormData.headName.trim()) {
      errors.headName = "Head Name is required";
    } else {
      const trimmedName = headFormData.headName.trim();
      const duplicateHead = currencyHeads.find(
        (head) =>
          head.headName.toLowerCase() === trimmedName.toLowerCase()
      );
      if (duplicateHead) {
        errors.headName = "A currency head with this name already exists";
      }
    }

    // Validate description
    if (!headFormData.description.trim()) {
      errors.description = "Description is required";
    }

    setHeadErrors(errors);

    if (errors.headName || errors.description) return;

    try {
      const payload = {
        headName: headFormData.headName.trim(),
        description: headFormData.description.trim(),
        isActive: headFormData.isActive,
        isUnit: headFormData.isUnit,
        createdBy: 1, // 🔥 replace with logged-in user id
      };

      const res = await createCurrencyHead(payload);
      // ✅ Option 1: use API response
      const newHead: CurrencyHead = {
        id: res?.data?.id || Date.now().toString(),
        headName: payload.headName,
        description: payload.description,
        isActive: payload.isActive,
        isUnit: payload.isUnit,
        currencies: [],
      };

      setCurrencyHeads((prev) => [...prev, newHead]);

      toast.success("Currency head added successfully");

      setIsAddHeadDialogOpen(false);
      setHeadFormData({ headName: "", description: "", isActive: true, isUnit: false });
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const message = error.response.data.message || "Failed to add currency head";
        toast.error(message);
      }
    }
  };


  // EDIT HEAD
  const handleEditHead = async () => {
    const errors = {
      headName: "",
      description: "",
    };

    // Validate head name
    if (!headFormData.headName.trim()) {
      errors.headName = "Head Name is required";
    } else {
      // Check for duplicate head names (case-insensitive), excluding the current head being edited
      const trimmedName = headFormData.headName.trim();
      const duplicateHead = currencyHeads.find(
        (head) =>
          head.id !== editingHead?.id &&
          head.headName.toLowerCase() === trimmedName.toLowerCase(),
      );
      if (duplicateHead) {
        errors.headName = "A currency head with this name already exists";
      }
    }

    // Validate description
    if (!headFormData.description.trim()) {
      errors.description = "Description is required";
    }

    setHeadErrors(errors);

    if (errors.headName || errors.description) return;

    if (!editingHead) {
      toast.error("No head selected for editing");
      return;
    }
    setLoading(true)

    try {
      await UpdateCurrencyHead({
        publicId: String(editingHead.publicId),
        headName: headFormData.headName.trim(),
        description: headFormData.description.trim(),
        isActive: headFormData.isActive,
        isUnit: headFormData.isUnit,
        updatedBy: user?.id
      });

      //UPDATE UI AFTER SUCCESS
      setCurrencyHeads((prev) =>
        prev.map((head) =>
          head.publicId === editingHead.publicId
            ? {
              ...head,
              headName: headFormData.headName.trim(),
              description: headFormData.description.trim(),
              isActive: headFormData.isActive,
              isUnit: headFormData.isUnit,
            }
            : head
        )
      );
      toast.success("Currency head updated successfully");
      setIsEditHeadDialogOpen(false);
      setEditingHead(null);
      setHeadFormData({ headName: "", description: "", isActive: true, isUnit: false });
      setLoading(false)
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const message = error.response.data.message || "Failed to update currency head";
        toast.error(message);
      }


    }
  };

  // Delete head
  const handleDeleteHead = (head: CurrencyHead) => {
    setDeleteTarget({ type: "head", headId: head?.publicId });
    setDeletingItemName(head.headName);
    setDeleteConfirmOpen(true);
  };


  const confirmDeleteHead = async () => {
    if (deleteTarget?.type === "head" && deleteTarget?.headId) {
      try {
        await deleteCurrencyHead(deleteTarget?.headId, String(user?.id)); // replace 1 with logged-in user id
        setCurrencyHeads((prev) =>
          prev.filter((h) => h.id !== deleteTarget.headId)
        );

        if (selectedHead?.id === deleteTarget.headId) {
          setSelectedHead(null);
          setIsAddCurrencyDialogOpen(false);
        }
        setRefatch(!refetch)
        toast.success("Currency head deleted successfully");
      } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.response?.data) {
          const message = error.response.data.message || "Failed to delete currency head";
          toast.error(message);
        }
      }
    }

    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
    setDeletingItemName(null);
  };

  // Add currency to head
  const resetAddCurrencyForm = () => {
    setCurrencyFormData({ currencyId: 0, currencyCode: "", currencySymbol: "", unitRate: "" });
    setCurrencyErrors({ currencyCode: "", unitRate: "" });
  };


  const handleAddCurrency = async () => {
    const errors = {
      currencyCode: "",
      unitRate: "",
    };

    if (!currencyFormData.currencyCode) {
      errors.currencyCode = "Select currency is required";
    }

    if (!currencyFormData.unitRate.trim()) {
      errors.unitRate = "Enter unit rate";
    }

    setCurrencyErrors(errors);

    if (errors.currencyCode || errors.unitRate) return;

    if (!selectedHead) return;

    const currencyExists = selectedHead.currencies.some(
      (c) => c.currencyCode === currencyFormData.currencyCode
    );

    if (currencyExists) {
      toast.error("This currency already exists");
      return;
    }

    try {
      await addCurrencyToHead(
        String(selectedHead.publicId),
        Number(user?.id) || 1,
        {
          currencyId: Number(currencyFormData.currencyId),
          unitRate: Number(currencyFormData.unitRate),
        }
      );

      // ✅ UPDATE UI
      const newCurrency: HeadCurrency = {
        id: Date.now().toString(),
        currencyCode: currencyFormData.currencyCode,
        currencySymbol: currencyFormData.currencySymbol,
        unitRate: currencyFormData.unitRate,
      };

      setCurrencyHeads((prev) =>
        prev.map((head) =>
          head.publicId === selectedHead.publicId
            ? { ...head, currencies: [...head.currencies, newCurrency] }
            : head
        )
      );
      toast.success("Currency added successfully");
      setRefatch(!refetch)
      setIsAddCurrencyDialogOpen(false);

      setCurrencyFormData({
        currencyId: 0,
        currencyCode: "",
        currencySymbol: "",
        unitRate: "",
      });

      setCurrencyErrors({
        currencyCode: "",
        unitRate: "",
      });
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const message = error.response.data.message || "Failed to Add currency";
        toast.error(message);
      }
    }
  };

  // Start editing unit rate inline
  const startEditingUnitRate = (currencyId: string, currentRate: string) => {
    setEditingCurrencyId(currencyId);
    setEditingUnitRate(currentRate);
  };


  // Save unit rate
  const saveUnitRate = async (headId: string, currencyId: string, currencyPublicId: string) => {
    if (!editingUnitRate.trim()) {
      toast.error("Unit rate cannot be empty");
      return;
    }

    try {
      setLoading(true)
      // 🔥 API CALL
      await UpdateCurrencyToHead({
        publicId: currencyPublicId,
        currencyId: Number(currencyId),
        unitRate: Number(editingUnitRate),
        updatedBy: Number(user?.id) || 1,
      });

      // ✅ update UI after success
      setCurrencyHeads((prev) =>
        prev.map((head) =>
          head.id === headId
            ? {
              ...head,
              currencies: head.currencies.map((c) =>
                c.publicId === currencyPublicId
                  ? { ...c, unitRate: editingUnitRate }
                  : c
              ),
            }
            : head
        )
      );

      toast.success("Unit rate updated successfully");
      setEditingCurrencyId(null);
      setEditingUnitRate("");
      setLoading(false)
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const message = error.response.data.message || "Failed to update unit rate";
        toast.error(message);
      }
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingCurrencyId(null);
    setEditingUnitRate("");
  };


  // Delete currency
  const handleDeleteCurrency = (
    headId: string,
    currencyId: string,
    currencyCode: string,
  ) => {
    setDeleteTarget({ type: "currency", headId, currencyId });
    setDeletingItemName(currencyCode);
    setDeleteConfirmOpen(true);
  };


  const confirmDeleteCurrency = async () => {
    if (
      deleteTarget?.type === "currency" &&
      deleteTarget?.headId &&
      deleteTarget?.currencyId // ✅ change this
    ) {
      try {
        await deleteCurrency(deleteTarget.currencyId);
        setRefatch(!refetch)
        setCurrencyHeads((prev) =>
          prev.map((head) =>
            head.id === deleteTarget.headId
              ? {
                ...head,
                currencies: head.currencies.filter(
                  (c) => c.publicId !== deleteTarget.currencyId
                ),
              }
              : head
          )
        );
        toast.success("Currency deleted successfully");
      } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.response?.data) {
          const message = error.response.data.message || "Validation failed";
          toast.error(message);
        }
      }
    }

    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
    setDeletingItemName(null);
  };

  // Open dialogs
  const openAddCurrencyDialog = (head: CurrencyHead) => {
    setSelectedHead(head);
    setCurrencyFormData({ currencyId: 0, currencyCode: "", currencySymbol: "", unitRate: "" });
    setHeadErrors({ headName: "", description: "" });
    setIsAddCurrencyDialogOpen(true);
  };

  const openEditHeadDialog = (head: CurrencyHead) => {
    setEditingHead(head);
    setHeadFormData({
      headName: head.headName,
      description: head.description,
      isActive: head.isActive,
      isUnit: head.isUnit ?? false,
    });
    setHeadErrors({ headName: "", description: "" });
    setIsEditHeadDialogOpen(true);
  };

  const openAddHeadDialog = () => {
    setHeadFormData({ headName: "", description: "", isActive: true, isUnit: false });
    setHeadErrors({ headName: "", description: "" });
    setIsAddHeadDialogOpen(true);
  };


  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      {loading && <FullScreenLoading />}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            placeholder="Search currency heads or currencies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full"
          />
        </div>
        <Button
          onClick={openAddHeadDialog}
          style={{ background: "#5B9BD5" }}
          className="text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Currency Head
        </Button>
      </div>

      <div style={{ marginTop: "10px" }}>
        <p className="text-gray-600">
          Manage currency heads with multiple currency unit rates (
          {Array.isArray(currencyHeads) ? currencyHeads.length : 0} heads)
        </p>
      </div>

      {/* Desktop Table */}
      <Card className="bg-white border-[#5B9BD5]/30 hidden md:block">
        <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50" style={{ padding: "8px 15px 0px 15px"}}>
          <CardTitle style={{ color: "#5B9BD5" }}>
            Currency Heads & Currencies
          </CardTitle>
          <CardDescription className="pb-2">
            {Array.isArray(currencyHeads) ? currencyHeads.length : 0} currency head
            {Array.isArray(currencyHeads) && currencyHeads.length !== 1 ? "s" : ""} found with{" "}
            {Array.isArray(currencyHeads)
              ? currencyHeads.reduce(
                (sum, head) => sum + (head.currencies?.length ?? 0),
                0,
              )
              : 0}{" "}
            currencies
          </CardDescription>
        </CardHeader>
        <CardContent style={{ padding: "0px 0px 10px 0px" }}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer hover:bg-sky-100"
                    onClick={() => handleSort("headName")}
                  >
                    Head Name {getSortIcon("headName")}
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer hover:bg-sky-100"
                    onClick={() => handleSort("currency")}
                  >
                    Currency {getSortIcon("currency")}
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs"
                  >
                    Unit Rate
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs text-center"
                  >
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!Array.isArray(currencyHeads) || currencyHeads.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center py-8 px-4 text-gray-500 text-sm"
                    >
                      No currency heads found
                    </TableCell>
                  </TableRow>
                ) : (
                  currencyHeads.flatMap((head, headIndex) => {
                    const currencies = Array.isArray(head?.currencies)
                      ? head.currencies
                      : [];

                    // Head row (only if has currencies)
                    if (currencies.length > 0) {
                      return currencies.map((currency, index) => (
                        <TableRow
                          key={`head-${head.publicId ?? head.id ?? index}-currency-${currency.id ?? currency.currencyCode ?? index}`}
                          className="hover:bg-sky-50/30"
                        >
                          {/* Head Name - only show for first currency */}
                          {index === 0 ? (
                            <TableCell
                              className="px-4 py-3 border-r-2 border-gray-300 bg-gray-50/50"
                              rowSpan={currencies.length}
                            >
                              <div className="space-y-1">
                                <span className="text-gray-900 text-sm font-medium block">
                                  {head.headName}
                                </span>
                                <div style={{ marginTop: "0px" }}>
                                  <span className="text-xs text-gray-600">
                                    {head.description || "N/A"}
                                  </span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <div>
                                    <Badge
                                      className={`${head.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                                    >
                                      {head.isActive ? "Active" : "Inactive"}
                                    </Badge>
                                  </div>
                                  <div>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openEditHeadDialog(head)}
                                      className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                      title="Edit head"
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </Button>

                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteHead(head)}
                                      className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                                      title="Delete head"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                          ) : null}

                          {/* Currency */}
                          <TableCell className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">
                                {currency.currencySymbol}
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {currency.currencyCode}
                              </span>
                            </div>
                          </TableCell>

                          {/* Unit Rate - Inline Editable */}
                          <TableCell className="px-4 py-3">
                            {editingCurrencyId === currency.id ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-600 whitespace-nowrap">
                                  1 unit =
                                </span>
                                <Input
                                  type="number"
                                  value={editingUnitRate}
                                  onChange={(e) =>
                                    setEditingUnitRate(e.target.value)
                                  }
                                  className="h-7 w-24 text-xs"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter")
                                      saveUnitRate(head?.id,
                                        String(currency.currencyId),
                                        String(currency.publicId));
                                    if (e.key === "Escape") cancelEditing();
                                  }}
                                />
                                <span className="text-xs text-gray-600">
                                  {currency.currencyCode}
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    saveUnitRate(head?.id,
                                      String(currency.currencyId),
                                      String(currency.publicId))
                                  }
                                  className="text-green-600 hover:bg-green-50 h-6 w-6 p-0"
                                  title="Save"
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={cancelEditing}
                                  className="text-gray-600 hover:bg-gray-50 h-6 w-6 p-0"
                                  title="Cancel"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <div
                                  className="flex items-center gap-1 cursor-pointer hover:bg-gray-100 rounded px-2 py-1 flex-1"
                                  onClick={() =>
                                    startEditingUnitRate(
                                      currency.id,
                                      currency.unitRate,
                                    )
                                  }
                                  title="Click to edit"
                                >
                                  <span className="text-xs text-gray-600">
                                    1 unit =
                                  </span>
                                  <Badge
                                    variant="outline"
                                    className="border-purple-300 text-purple-700 bg-purple-50 text-[10px] px-1.5 py-0.5"
                                  >
                                    {currency.unitRate} {currency.currencyCode}
                                  </Badge>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    handleDeleteCurrency(
                                      String(head?.publicId),
                                      String(currency.publicId),
                                      // String(currency.currencyId),
                                      currency.currencyCode,
                                    )
                                  }
                                  className="text-red-600 hover:bg-red-50 h-6 w-6 p-0"
                                  title="Delete currency"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                          </TableCell>

                          {/* Description - only show for first currency */}

                          {/* Actions */}
                          {index === 0 && (head.isUnit) && (
                            <TableCell
                              rowSpan={currencies.length}
                              className="px-4 py-3 align-middle text-right border-l-2 border-gray-300"
                            >
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openAddCurrencyDialog(head)}
                                  className="text-green-600 hover:bg-green-50 h-7 w-7 p-0"
                                  title="Add currency"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                          {/* {(head.isUnit) && (
                            <TableCell
                              rowSpan={currencies.length}
                              className="px-4 py-3 align-middle text-right border-l-2 border-gray-300"
                            >
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openAddCurrencyDialog(head)}
                                  className="text-green-600 hover:bg-green-50 h-7 w-7 p-0"
                                  title="Add currencyyyy"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          )} */}
                        </TableRow>
                      ));
                    }

                    // If head has no currencies, show a single row
                    if (currencies.length === 0) {
                      return [
                        <TableRow key={`head-${head.publicId ?? head.id ?? headIndex}`} className="hover:bg-sky-50/30">
                          <TableCell className="px-4 py-3 border-r-2 border-gray-300 bg-gray-50/50">
                            <div className="space-y-1">
                              <span className="text-gray-900 text-sm font-medium block">
                                {head.headName}
                              </span>
                              <div style={{ marginTop: "0px" }}>
                                <span className="text-xs text-gray-600">
                                  {head.description || "N/A"}
                                </span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "8px" }}>
                                <div>
                                  <Badge
                                    className={`${head.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                                  >
                                    {head.isActive ? "Active" : "Inactive"}
                                  </Badge>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openEditHeadDialog(head)}
                                    className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                    title="Edit head"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteHead(head)}
                                    className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                                    title="Delete head"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-3" colSpan={2}>
                            <span className="text-xs text-gray-400 italic">
                              No currencies added
                            </span>
                          </TableCell>
                          {(head.isUnit) && (
                            <TableCell className="px-4 py-3 align-middle text-right border-l-2 border-gray-300">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openAddCurrencyDialog(head)}
                                  className="text-green-600 hover:bg-green-50 h-7 w-7 p-0"
                                  title="Add currency"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ];
                    }
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-4">
        {!Array.isArray(currencyHeads) || currencyHeads.length === 0 ? (
          <Card className="bg-white border-[#5B9BD5]/30">
            <CardContent className="p-6 text-center text-gray-500 text-sm">
              No currency heads found
            </CardContent>
          </Card>
        ) : (
          currencyHeads?.map((head, headIndex) => {
            const currencies = Array.isArray(head?.currencies) ? head.currencies : [];
            return (
              <Card
                key={`head-${head.publicId ?? head.id ?? headIndex}`}
                className="bg-white border-[#5B9BD5]/30"
                style={{ gap: "0px" }}
              >
                <CardHeader className="pb-3 bg-gradient-to-r from-sky-50 to-blue-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-base text-gray-900">
                        {head.headName}
                      </CardTitle>
                      {head.description && (
                        <CardDescription className="mt-1 text-xs">
                          {head.description}
                        </CardDescription>
                      )}
                    </div>
                    <Badge
                      className={`${head.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"} text-[10px] shrink-0 ml-2`}
                    >
                      {head.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-3 space-y-3">
                  {/* Currencies */}
                  {currencies.length > 0 ? (
                    <div className="space-y-2">
                      {currencies.map((currency, currencyIndex) => (
                        <div
                          key={`head-${head.publicId ?? head.id ?? headIndex}-currency-${currency.id ?? currency.currencyCode ?? currencyIndex}`}
                          className="p-2 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">
                                {currency.currencySymbol}
                              </span>
                              <span className="text-sm font-medium text-gray-900">
                                {currency.currencyCode}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                handleDeleteCurrency(
                                  String(head?.publicId),
                                  String(currency.publicId),
                                  // String(currency.currencyId),
                                  currency.currencyCode,
                                )
                              }
                              className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          {editingCurrencyId === currency.id ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-600 whitespace-nowrap">
                                1 unit =
                              </span>
                              <Input
                                type="number"
                                value={editingUnitRate}
                                onChange={(e) =>
                                  setEditingUnitRate(e.target.value)
                                }
                                className="h-8 flex-1 text-xs"
                                autoFocus
                              />
                              <span className="text-xs text-gray-600">
                                {currency.currencyCode}
                              </span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => saveUnitRate(head.id,
                                  String(currency.currencyId),
                                  String(currency?.publicId)
                                )}
                                className="text-green-600 hover:bg-green-50 h-7 w-7 p-0"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelEditing}
                                className="text-gray-600 hover:bg-gray-50 h-7 w-7 p-0"
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <div
                              className="flex text-xs text-gray-600 cursor-pointer hover:bg-gray-200 rounded p-1 gap-4"
                              onClick={() =>
                                startEditingUnitRate(
                                  currency.id,
                                  currency.unitRate,
                                )
                              }
                            >
                              1 unit = {currency.unitRate} {currency.currencyCode}{" "}
                              <span className="text-[#5B9BD5]">
                                <Edit2 className="w-3.5 h-3.5 mr-1" />
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic text-center py-2">
                      No currencies added
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-4" style={{ marginTop: "0px" }}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openAddCurrencyDialog(head)}
                      className="flex-1 border-green-300 text-green-700 hover:bg-green-50"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      Add Currency
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditHeadDialog(head)}
                      className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                    >
                      <Edit2 className="w-3.5 h-3.5 mr-1" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteHead(head)}
                      className="border-red-300 text-red-600 hover:bg-red-50 px-2"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Add Currency Head Dialog */}
      <Dialog open={isAddHeadDialogOpen} onOpenChange={setIsAddHeadDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle style={{ color: "#5B9BD5" }}>
              Add Currency Head
            </DialogTitle>
            <DialogDescription>
              Create a new currency head. You can add currencies to it later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <InputFieldset
                required
                type='text'
                label="Head Name "
                id="head-name"
                placeholder="e.g., Mannat - General"
                value={headFormData.headName}
                onChange={(e) => {
                  const value = removeSpecialCharacters(e.target.value);
                  setHeadFormData({ ...headFormData, headName: value });
                  if (e.target.value) {
                    setHeadErrors({ ...headErrors, headName: "" });
                  }
                }}
                className={
                  headErrors.headName ? "border-red-500 focus:ring-red-500" : ""
                }
              />
              {headErrors.headName && (
                <p className="text-red-500 text-xs mt-1">
                  {headErrors.headName}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <SelectFieldset label="Description" required>
                <TextareaUI
                  id="head-description"
                  placeholder="Optional description..."
                  value={headFormData.description}
                  onChange={(e) => {
                    setHeadFormData({ ...headFormData, description: e.target.value })
                    setHeadErrors({ ...headErrors, description: "" });
                  }}
                  className="border-gray-300 min-h-[100px]"
                  rows={4}
                />
              </SelectFieldset>
              {headErrors.description && (
                <p className="text-red-500 text-xs mt-1">
                  {headErrors.description}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="head-active">Active Status</Label>
                <Switch
                  id="head-active"
                  checked={headFormData.isActive}
                  onCheckedChange={(checked) =>
                    setHeadFormData({ ...headFormData, isActive: checked })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="head-is-unit">Is Unit</Label>
                <Switch
                  id="head-is-unit"
                  checked={headFormData.isUnit}
                  onCheckedChange={(checked) =>
                    setHeadFormData({ ...headFormData, isUnit: checked })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddHeadDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddHead}
              style={{ background: "#5B9BD5" }}
              className="text-white"
            >
              Add Head
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Currency Head Dialog */}
      <Dialog
        open={isEditHeadDialogOpen}
        onOpenChange={setIsEditHeadDialogOpen}
      >
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle style={{ color: "#5B9BD5" }}>
              Edit Currency Head
            </DialogTitle>
            <DialogDescription>Update currency head details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <InputFieldset
                required
                type='text'
                label="Head Name "
                id="edit-head-name"
                placeholder="e.g., Mannat - General"
                value={headFormData.headName}
                onChange={(e) => {
                  const value = removeSpecialCharacters(e.target.value);
                  setHeadFormData({ ...headFormData, headName: value });
                  if (e.target.value) {
                    setHeadErrors({ ...headErrors, headName: "" });
                  }
                }}
                className={
                  headErrors.headName ? "border-red-500 focus:ring-red-500" : ""
                }
              />
              {headErrors.headName && (
                <p className="text-red-500 text-xs mt-1">
                  {headErrors.headName}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <SelectFieldset label="Description" required>
                <TextareaUI
                  id="edit-head-description"
                  placeholder="Optional description..."
                  value={headFormData.description}
                  onChange={(e) => {
                    setHeadFormData({ ...headFormData, description: e.target.value })
                    setHeadErrors({ ...headErrors, description: "" });
                  }}
                  className="border-gray-300 min-h-[100px]"
                  rows={4}
                />
              </SelectFieldset>
              {headErrors.description && (
                <p className="text-red-500 text-xs mt-1">
                  {headErrors.description}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-head-active">Active Status</Label>
                <Switch
                  id="edit-head-active"
                  checked={headFormData.isActive}
                  onCheckedChange={(checked) =>
                    setHeadFormData({ ...headFormData, isActive: checked })
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="isUnit-active">Is Unit</Label>
                <Switch
                  id="isUnit-active"
                  checked={headFormData.isUnit}
                  onCheckedChange={(checked) =>
                    setHeadFormData({ ...headFormData, isUnit: checked })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditHeadDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditHead}
              style={{ background: "#5B9BD5" }}
              className="text-white"
            >
              Update Head
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Currency Dialog */}
      <Dialog
        open={isAddCurrencyDialogOpen}
        onOpenChange={(open) => {
          setIsAddCurrencyDialogOpen(open);
          if (!open) {
            resetAddCurrencyForm();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: "#5B9BD5" }}>Add Currency</DialogTitle>
            <DialogDescription>
              Add a currency to {selectedHead?.headName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="currency-select">
                Currency <span className="text-red-500">*</span>
              </Label>

              <Select
                value={currencyFormData.currencyId?.toString()}
                onValueChange={(value) => {
                  const selectedCurrency = currencies.find(
                    (c) => c.currencyId === Number(value)
                  );

                  setCurrencyFormData({
                    ...currencyFormData,
                    currencyId: Number(value),
                    currencyCode: selectedCurrency?.currencyCode || "",
                    currencySymbol: selectedCurrency?.currencySymbol || "",
                  });

                  setCurrencyErrors({ ...currencyErrors, currencyCode: "" });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>

                <SelectContent>
                  {currencies.map((curr) => (
                    <SelectItem
                      key={curr.currencyId}
                      value={curr.currencyId.toString()} // ✅ use ID
                    >
                      {curr.currencySymbol} {curr.currencyCode} - {curr.currencyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {currencyErrors.currencyCode && (
                <p className="text-red-500 text-xs mt-1">
                  {currencyErrors.currencyCode}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit-rate">
                Unit Rate <span className="text-red-500">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">1 unit =</span>
                <Input
                  id="unit-rate"
                  type="number"
                  placeholder="100"
                  value={currencyFormData.unitRate}
                  onChange={(e) => {
                    setCurrencyFormData({
                      ...currencyFormData,
                      unitRate: e.target.value,
                    });
                    setCurrencyErrors({ ...currencyErrors, unitRate: "" });
                  }}
                  className={`flex-1 ${currencyErrors.unitRate ? "border-red-500 focus:ring-red-500" : ""}`}
                />

                {currencyErrors.unitRate && (
                  <p className="text-red-500 text-xs mt-1">
                    {currencyErrors.unitRate}
                  </p>
                )}
                <span className="text-sm text-gray-600">
                  {currencyFormData.currencyCode || "Currency"}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddCurrencyDialogOpen(false);
                resetAddCurrencyForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddCurrency}
              style={{ background: "#5B9BD5" }}
              className="text-white"
            >
              Add Currency
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              Delete{" "}
              {deleteTarget?.type === "head" ? "Currency Head" : "Currency"}
            </DialogTitle>

            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-semibold">"{deletingItemName}"</span>? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setDeleteTarget(null);
                setDeletingItemName(null);
              }}
            >
              No, Cancel
            </Button>

            <Button
              onClick={
                deleteTarget?.type === "head"
                  ? confirmDeleteHead
                  : confirmDeleteCurrency
              }
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};